import { isEditorialOnly } from './predicates';
import type { SearchParameters } from './searchParameters';
import { AssetFamilySearchFilterEnum } from './filterValues';

/**
 * An event search is an editorial search: states `family=editorial` whenever
 * the search names an event.
 *
 * One-directional. It only ever adds the family to a search that named an
 * event — an editorial search with no event is untouched, and nothing here
 * reads the family to conclude anything about events.
 *
 * Events are editorial content, so `?events=776359&family=creative` is a search
 * that can return nothing. Naming an event is the more specific ask, so the
 * family yields to it rather than the other way round — and that holds however
 * the family got there, so a mixed creative-and-editorial family narrows too.
 *
 * Narrowing is not cosmetic. `editorialproducts` and `specificpeople` are only
 * available on an editorial-only family, so an event search left mixed loses
 * them: a stated one is cleared further down the chain and the visitor loses a
 * filter the url asked for.
 *
 * Availability resolves the same contradiction in the opposite
 * direction: `events` is unavailable on a creative-only family, so
 * `clearUnavailable` would drop the event and keep the family. That
 * discards what the visitor actually asked for, so this rung runs first and
 * leaves the family already editorial by the time availability is asked.
 *
 * Returns `searchParameters` itself when the family is editorial already.
 */
export const applyEditorialFamilyForEvents = (
  searchParameters: SearchParameters
): SearchParameters =>
  searchParameters.events && !isEditorialOnly(searchParameters)
    ? searchParameters.with({ family: AssetFamilySearchFilterEnum.EDITORIAL })
    : searchParameters;
